import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../../services/api';
import { Users, Search } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';

export default function Customers() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: vendorData, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => {
      const response = await vendorApi.me();
      return response.data;
    },
  });

  const { data: customersData, isLoading } = useQuery({
    queryKey: ['vendor', 'customers'],
    queryFn: async () => {
      const response = await vendorApi.getCustomers();
      return response.data;
    },
  });

  useEffect(() => {
    if (error) {
      navigate('/login');
    }
  }, [error, navigate]);

  const handleLogout = async () => {
    try {
      await vendorApi.logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/login');
    }
  };

  // Customers are identified by customer number (the list's SystemId is regenerated on every call)
  const handleRowClick = (customerNo: string) => {
    navigate(`/customers/${encodeURIComponent(customerNo)}`);
  };

  // Aggregate customers by customerNo and filter based on search
  const aggregatedCustomers = () => {
    if (!customersData?.customers) return [];

    // Group customers by customerNo
    const customerMap = new Map();
    customersData.customers.forEach((customer: any) => {
      const key = customer.customerNo;
      if (customerMap.has(key)) {
        const existing = customerMap.get(key);
        existing.settlementOrderAmount += (customer.settlementOrderAmount || 0);
        if (customer.amountThisYear !== undefined) existing.amountThisYear = (existing.amountThisYear || 0) + customer.amountThisYear;
      } else {
        customerMap.set(key, {
          customerNo: customer.customerNo,
          name: customer.customerName || customer.name,
          city: customer.city,
          systemId: customer.systemId,
          settlementOrderAmount: customer.settlementOrderAmount || 0,
          // Sent by Settled.Portal 1.5.0.13+; undefined before that
          amountThisYear: customer.amountThisYear
        });
      }
    });

    return Array.from(customerMap.values());
  };

  const filteredCustomers = aggregatedCustomers().filter((customer: any) => {
    const matchesSearch =
      customer.customerNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.city?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  }) || [];

  const formatCurrency = (amount: number) => {
    return amount?.toLocaleString('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) || '0 SEK';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Laddar kunder...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar
        logoUrl={vendorData?.tenant?.logoUrl}
        companyName={vendorData?.tenant?.companyName}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <TopBar
          userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username}
          onLogout={handleLogout}
        />

        {/* Page Content */}
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          <div className="mb-6">
            <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Kunder</h1>
            <p className="text-gray-600 mt-1">Visa och hantera dina kunder</p>
          </div>

          {/* Filters */}
          <div
            className="panel mb-6"
          >
            <div className="bg-surface p-4">
            <div className="grid grid-cols-1 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Sök efter kundnr, namn, stad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-4 text-sm text-gray-600">
              Visar {filteredCustomers.length} av {aggregatedCustomers().length} kunder
            </div>
            </div>
          </div>

          {/* Customers Table */}
          <div className="card overflow-hidden">
            {filteredCustomers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kundnr
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Namn
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stad
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaktioner {new Date().getFullYear()}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaktioner totalt
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCustomers.map((customer: any) => (
                      <tr
                        key={customer.customerNo}
                        onClick={() => handleRowClick(customer.customerNo)}
                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono font-medium text-gray-900">
                            {customer.customerNo}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">{customer.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{customer.city || '-'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right tabular-nums">
                          <span className="text-sm text-gray-900">
                            {customer.amountThisYear === undefined ? '–' : formatCurrency(customer.amountThisYear)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right tabular-nums">
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(customer.settlementOrderAmount || 0)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="mb-2 text-base font-semibold text-ink">Inga kunder hittades</h3>
                <p className="text-gray-600">
                  {searchTerm
                    ? 'Prova att justera dina sökkriterier'
                    : 'Inga kunder tillgängliga för ditt konto'}
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
